import 'react-toastify/dist/ReactToastify.css';
import './app.scss';
import 'app/config/dayjs';

import React, { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import Sidebar from './shared/layout/sidebar/sidebar';
import { useAppDispatch, useAppSelector } from '../../../app/config/store';
import { getSession, startSessionWatcher } from '../app/shared/reducers/authentication';
import { getProfile } from '../app/shared/reducers/application-profile';
import { hasAnyAuthority } from '../../../app/shared/auth/private-route';
import ErrorBoundary from '../../../app/shared/error/error-boundary';
import { AUTHORITIES } from '../app/config/constants';
import AppRoutes from '../app/routes';
import ReactGA from 'react-ga4';
import Stepperform from './shared/layout/stepperform/stepperform';
import { logout } from '../../../app/shared/reducers/authentication';
import { initAmplitude, identifyUser } from './config/amplitude';

const baseHref = document.querySelector('base').getAttribute('href').replace(/\/$/, '');

export const App = () => {
  useEffect(() => {
    initAmplitude();
  }, []);
  const dispatch = useAppDispatch();
  const session_expire = 24 * 60 * 60 * 1000;
  const GA_MEASUREMENT_ID = process.env.IS_DEV ? 'G-XXXXX' : 'G-XB3BLFW4DP';
  useEffect(() => {
    ReactGA.initialize(GA_MEASUREMENT_ID);
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname + location.search,
      title: document.title || 'Home',
    });

    dispatch(getSession());
    dispatch(getProfile());
  }, []);

  const currentLocale = useAppSelector(state => state.locale.currentLocale);
  const caseReview = useAppSelector(state => state.caseReview);
  const isAuthenticated = useAppSelector(state => state.authentication.isAuthenticated);
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const ribbonEnv = useAppSelector(state => state.applicationProfile.ribbonEnv);
  const isInProduction = useAppSelector(state => state.applicationProfile.inProduction);
  const isOpenAPIEnabled = useAppSelector(state => state.applicationProfile.isOpenAPIEnabled);
  const isLoading = useAppSelector(state => state.authentication.loading);
  const [isSidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const { firstName, lastName, email, id } = useAppSelector(state => state.authentication?.account);

  useEffect(() => {
    if (isAuthenticated) {
      identifyUser({
        id: email,
        email: email,
        name: firstName + lastName,
      });
    }
  }, [id]);

  useEffect(() => {
    if (isAuthenticated) {
      const loginTime = localStorage.getItem('loginTime');
      if (!loginTime) {
        localStorage.setItem('loginTime', Date.now().toString());
      }
    } else {
      localStorage.removeItem('loginTime');
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const cleanup: any = dispatch(startSessionWatcher());
    return () => cleanup?.();
  }, [dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      const loginTime = localStorage.getItem('loginTime');
      const now = Date.now();

      if (loginTime && now - parseInt(loginTime, 10) > session_expire) {
        dispatch(logout());
        toast.info('Session expired. You have been logged out automatically.', { position: 'top-right' });
        localStorage.removeItem('loginTime');
      }
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const toggleSidebar = () => {
    const newSidebarState = !isSidebarCollapsed;
    setSidebarCollapsed(newSidebarState);
    localStorage.setItem('sidebarCollapsed', newSidebarState.toString());
  };

  const paddingTop = '0px';
  return (
    <BrowserRouter basename={baseHref}>
      <div className={`${isAuthenticated ? 'app-container' : 'terms-condition'}`} style={{ paddingTop }}>
        <ToastContainer position="top-left" className="toastify-container" toastClassName="toastify-toast" />
        <div className={`container-fluid view-container ${isSidebarCollapsed ? 'full-view-content' : ''}`} id="app-view-container">
          {isAuthenticated && (
            <div className="app-container">
              <div>
                <Sidebar
                  isAuthenticated={isAuthenticated}
                  isAdmin={isAdmin}
                  currentLocale={currentLocale}
                  ribbonEnv={ribbonEnv}
                  isInProduction={isInProduction}
                  isOpenAPIEnabled={isOpenAPIEnabled}
                  isSidebarCollapsed={isSidebarCollapsed}
                  toggleSidebar={toggleSidebar}
                />
              </div>
            </div>
          )}
        </div>
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
      </div>
    </BrowserRouter>
  );
};

export default App;
